import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import heroImage from "../assets/ema-hero.jpeg";

import { db } from "../config/firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";

export default function Home() {
  /* =========================================================
     ACTUALITÉS FIREBASE
  ========================================================= */

  const [firebaseAnnouncements, setFirebaseAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(true);

  useEffect(() => {
    async function fetchAnnouncements() {
      try {
        const q = query(
          collection(db, "announcements"),
          orderBy("published_at", "desc")
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((firebaseDoc) => ({
          id: firebaseDoc.id,
          ...firebaseDoc.data(),
        }));

        setFirebaseAnnouncements(data);
      } catch (error) {
        console.error(
          "Erreur chargement des actualités Firebase :",
          error
        );
      } finally {
        setLoadingAnnouncements(false);
      }
    }

    fetchAnnouncements();
  }, []);

  /*
    Trier les annonces par date.
    Les 4 dernières alimentent la page d'accueil.
  */
  const latestAnnouncements = useMemo(() => {
    return [...firebaseAnnouncements]
      .sort((a, b) => {
        const dateB = new Date(b.published_at || 0).getTime();
        const dateA = new Date(a.published_at || 0).getTime();

        return dateB - dateA;
      })
      .slice(0, 4);
  }, [firebaseAnnouncements]);

  const featuredHomeAnnouncement = latestAnnouncements[0] || null;

  const sideAnnouncements = latestAnnouncements.slice(1, 4);

  const formatDateFR = (value) => {
    if (!value) return "";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) return "";

    return d.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  /* =========================================================
     AFFICHAGE
  ========================================================= */

  return (
    <main className="ema">

      {/* =====================================================
          HERO FULL SCREEN
      ====================================================== */}

      <section
        className="ema-hero-full"
        style={{
          backgroundImage: `url(${heroImage})`,
        }}
      >
        <div className="ema-hero-overlay">
          <div className="ema-hero-content">

            <p className="ema-hero-eyebrow">
              Institution de formation judiciaire
            </p>

            <h1 className="ema-hero-title">
              École de la Magistrature
            </h1>

            <p className="ema-hero-subtitle">
              Former les magistrats, greffiers et acteurs de la Justice à travers
              une formation moderne, rigoureuse et centrée sur l’excellence.
            </p>

          </div>
        </div>
      </section>

      {/* =====================================================
          MOT DU DIRECTEUR
      ====================================================== */}

      <section className="ema-section">
        <div className="ema-container">

          <div className="ema-director">

            <div className="ema-director-photoWrap">
              <img
                src="/gouvernance/directeur.jpg"
                alt="Directeur de l’École de la Magistrature"
                className="ema-director-photo"
                onError={(e) => {
                  e.currentTarget.src =
                    "/gouvernance/placeholder.jpg";
                }}
              />
            </div>

            <div className="ema-director-body">

              <p className="ema-kicker">
                Mot du Directeur
              </p>

              <h2
                className="ema-h2"
                style={{ marginTop: 8 }}
              >
                Une formation judiciaire exigeante, au service de l’État de droit
              </h2>

              <p
                className="ema-paragraph"
                style={{ marginTop: 10 }}
              >
                Bienvenue sur la plateforme officielle de l’École de la
                Magistrature. Notre mission est de former des magistrats,
                greffiers et professionnels de la Justice avec rigueur, éthique
                et excellence, en combinant théorie, pratique, simulations
                d’audience et stages en juridiction.
              </p>

              <p
                className="ema-paragraph"
                style={{ marginTop: 10 }}
              >
                Nous vous invitons à découvrir nos programmes, nos activités et
                les opportunités offertes au sein de notre institution.
              </p>

              <p
                className="ema-muted"
                style={{ marginTop: 12 }}
              >
                <strong>
                  Me. Kesner Michel Thermesi
                </strong>{" "}
                — Directeur général ai. de l’École de la Magistrature
              </p>

              <div
                className="ema-inline-actions"
                style={{ marginTop: 14 }}
              >
                <Link
                  className="ema-btn ema-btn-secondary"
                  to="/gouvernance"
                >
                  Découvrir la gouvernance
                </Link>

                <Link
                  className="ema-btn ema-btn-ghost"
                  to="/formations"
                >
                  Voir les formations
                </Link>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* =====================================================
          CONSEIL DE DIRECTION
      ====================================================== */}

      <section className="ema-section">
        <div className="ema-container">

          <div className="ema-section-head">

            <div>
              <p className="ema-kicker">
                Gouvernance
              </p>

              <h2 className="ema-h2">
                Conseil d'Administration
              </h2>

              <p className="ema-lead">
                L’équipe dirigeante assure l’orientation stratégique, la qualité
                académique et la gestion administrative de l’École de la
                Magistrature.
              </p>
            </div>

            <Link
              className="ema-btn ema-btn-ghost"
              to="/gouvernance"
            >
              Voir la gouvernance →
            </Link>

          </div>

          <div className="ema-grid-3">

            <PersonCard
              role="Directeur général ai."
              name="Kesner Michel Thermesi"
              title="Magistrat / Chef d’établissement"
              photo="/gouvernance/directeur.jpg"
              photoPosition="center 22%"
              to="/gouvernance"
            />

            <PersonCard
              role="Directrice des études"
              name="Maguy Florestal"
              title="Responsable académique & pédagogie"
              summary="Magistrate chargée de l’orientation académique, de la coordination pédagogique et du suivi des programmes de formation."
              photo="/gouvernance/directrice-etudes.jpeg"
              photoPosition="center 18%"
              to="/gouvernance/maguy-florestal"
            />

            <PersonCard
              role="Administratrice"
              name="Jeanne Béanne Casty"
              title="Administration & finances"
              photo="/gouvernance/administratrice.jpeg"
              photoPosition="center 20%"
              to="/gouvernance"
            />

          </div>

        </div>
      </section>

      {/* =====================================================
          ACCÈS RAPIDES
      ====================================================== */}

      <section className="ema-section">
        <div className="ema-container">

          <h2 className="ema-h2">
            Accès rapides
          </h2>

          <p className="ema-lead">
            Les liens essentiels pour les candidats et le public.
          </p>

          <div className="ema-grid-4">

            <QuickCard
              title="Concours & conditions"
              text="Consultez les critères d’éligibilité, les pièces requises et les étapes du processus de sélection."
              to="/concours"
              cta="Voir les conditions"
            />

            <QuickCard
              title="Inscription en ligne"
              text="Créez votre compte et soumettez votre dossier en toute sécurité, avec un suivi en temps réel."
              to="/register"
              cta="Créer un compte"
            />

            <QuickCard
              title="Résultats & listes"
              text="Accédez aux listes officielles publiées par l’École : admissibilité, admission et convocations."
              to="/announcements"
              cta="Voir les résultats"
            />

            <QuickCard
              title="Convocations & documents"
              text="Téléchargez vos convocations, formulaires et documents utiles."
              to="/ressources"
              cta="Accéder aux documents"
            />

          </div>

        </div>
      </section>

      {/* =====================================================
          MISSION
      ====================================================== */}

      <section className="ema-section ema-section-alt">
        <div className="ema-container ema-grid-2">

          <div>

            <h2 className="ema-h2">
              Notre mission
            </h2>

            <p className="ema-paragraph">
              L’École de la Magistrature est une institution dédiée à la
              formation des professionnels de la Justice. Elle contribue à
              renforcer la compétence, l’éthique et l’efficacité du système
              judiciaire, en soutenant l’amélioration continue des pratiques et
              des standards.
            </p>

          </div>

          <div className="ema-panel">

            <h3 className="ema-h3">
              Nos missions
            </h3>

            <ul className="ema-list">

              <li>
                Assurer la{" "}
                <strong>
                  formation initiale
                </strong>{" "}
                des futurs magistrats et greffiers.
              </li>

              <li>
                Déployer une{" "}
                <strong>
                  formation continue
                </strong>{" "}
                adaptée aux évolutions du droit et des pratiques.
              </li>

              <li>
                Produire et diffuser des{" "}
                <strong>
                  ressources pédagogiques
                </strong>{" "}
                et guides professionnels.
              </li>

              <li>
                Développer la{" "}
                <strong>
                  coopération
                </strong>{" "}
                avec les institutions nationales et partenaires internationaux.
              </li>

            </ul>

          </div>

        </div>
      </section>

      {/* =====================================================
          FORMATIONS
      ====================================================== */}

      <section className="ema-section">
        <div className="ema-container">

          <div className="ema-section-head">

            <div>

              <h2 className="ema-h2">
                Nos programmes de formation
              </h2>

              <p className="ema-lead">
                Des parcours structurés combinant théorie, pratique, méthodologie
                et déontologie.
              </p>

            </div>

            <Link
              className="ema-btn ema-btn-secondary"
              to="/formations"
            >
              Voir toutes les formations
            </Link>

          </div>

          <div className="ema-grid-4">

            <ProgramCard
              title="Formation initiale (Magistrature)"
              text="Parcours complet : enseignements juridiques, cas pratiques, rédaction, éthique et simulations d’audience."
              to="/formations"
            />

            <ProgramCard
              title="Formation initiale (Greffe)"
              text="Procédures, organisation judiciaire, rédaction, rigueur administrative et compétences opérationnelles."
              to="/formations"
            />

            <ProgramCard
              title="Formation continue"
              text="Renforcement pour magistrats et greffiers : contentieux, management, technologies et déontologie."
              to="/formations"
            />

            <ProgramCard
              title="Formation probatoire"
              text="Préparation et consolidation des compétences professionnelles nécessaires à l’exercice des fonctions judiciaires."
              to="/formations"
            />

            <ProgramCard
              title="Séminaires & ateliers"
              text="Ateliers pratiques : rédaction des décisions, techniques d’audience et méthodologie du jugement."
              to="/formations"
            />

          </div>

        </div>
      </section>

      {/* =====================================================
          CONCOURS
      ====================================================== */}

      <section className="ema-section ema-section-alt">
        <div className="ema-container">

          <div className="ema-section-head">

            <div>

              <h2 className="ema-h2">
                Concours & admissions
              </h2>

              <p className="ema-lead">
                Un processus clair, sécurisé et transparent, conçu pour accompagner
                chaque candidat.
              </p>

            </div>

            <Link
              className="ema-btn ema-btn-primary"
              to="/register"
            >
              Démarrer une candidature
            </Link>

          </div>

          <div className="ema-timeline">

            <TimelineStep
              n="1"
              title="Vérifier son éligibilité"
              text="Prenez connaissance des conditions, pièces et délais officiels."
              to="/concours"
              cta="Lire les conditions"
            />

            <TimelineStep
              n="2"
              title="Créer son compte candidat"
              text="Enregistrez vos informations et sécurisez votre accès."
              to="/register"
              cta="Créer un compte"
            />

            <TimelineStep
              n="3"
              title="Soumettre le dossier + pièces"
              text="Téléversez vos documents, complétez votre candidature et validez."
              to="/candidate-dashboard"
              cta="Accéder au tableau de bord"
            />

            <TimelineStep
              n="4"
              title="Suivre l’évolution du dossier"
              text="Consultez l’état d’avancement, les notifications, convocations et résultats."
              to="/login"
              cta="Se connecter"
            />

          </div>

        </div>
      </section>

      {/* =====================================================
          ACTUALITÉS & COMMUNIQUÉS DYNAMIQUES
      ====================================================== */}

      <section className="ema-section">
        <div className="ema-container">

          <div className="ema-section-head">

            <div>

              <p className="ema-kicker">
                Actualités
              </p>

              <h2 className="ema-h2">
                Actualités & communiqués
              </h2>

              <p className="ema-lead">
                Suivez les annonces officielles, les activités académiques et les
                événements de l’École.
              </p>

            </div>

            <Link
              className="ema-btn ema-btn-secondary"
              to="/announcements"
            >
              Voir toutes les publications
            </Link>

          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 1fr)",
              gap: "24px",
              alignItems: "stretch",
            }}
            className="ema-home-news-grid"
          >

            {/* ===============================================
                À LA UNE
            ================================================ */}

            {featuredHomeAnnouncement ? (
              <Link
                to={
                  featuredHomeAnnouncement.link ||
                  "/announcements"
                }
                className="ema-news-feature"
                style={{
                  padding: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >

                {/* IMAGE */}
                <div
                  style={{
                    height: "300px",
                    position: "relative",
                    overflow: "hidden",
                    background: "#f3f4f6",
                  }}
                >

                  <img
                    src={
                      featuredHomeAnnouncement.image ||
                      featuredHomeAnnouncement.gallery?.[0] ||
                      "/media/photos/promotion8_4.jpg"
                    }
                    alt={featuredHomeAnnouncement.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />

                  {/* LÉGER OVERLAY */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.15))",
                    }}
                  />

                  {/* BADGES */}
                  <div
                    style={{
                      position: "absolute",
                      top: "18px",
                      left: "18px",
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >

                    <span
                      style={{
                        background: "#DC2626",
                        color: "#fff",
                        padding: "6px 12px",
                        borderRadius: "999px",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      À la une
                    </span>

                    {featuredHomeAnnouncement.category && (
                      <span
                        style={{
                          background: "#0B2C5F",
                          color: "#fff",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 700,
                        }}
                      >
                        {featuredHomeAnnouncement.category}
                      </span>
                    )}

                  </div>
                </div>

                {/* CONTENU */}
                <div
                  style={{
                    padding: "26px",
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >

                  <p
                    className="ema-news-date"
                    style={{
                      marginTop: 0,
                    }}
                  >
                    {formatDateFR(
                      featuredHomeAnnouncement.published_at
                    )}
                  </p>

                  <h3
                    className="ema-h3"
                    style={{
                      marginTop: "8px",
                      fontSize: "22px",
                      lineHeight: 1.35,
                      color: "#0B2C5F",
                    }}
                  >
                    {featuredHomeAnnouncement.title}
                  </h3>

                  {featuredHomeAnnouncement.summary && (
                    <p
                      className="ema-paragraph"
                      style={{
                        marginTop: "10px",
                        color: "#6B7280",
                        lineHeight: 1.7,
                      }}
                    >
                      {featuredHomeAnnouncement.summary}
                    </p>
                  )}

                  <span
                    className="ema-link"
                    style={{
                      marginTop: "auto",
                      paddingTop: "20px",
                      display: "inline-block",
                    }}
                  >
                    Lire la publication →
                  </span>

                </div>
              </Link>
            ) : (
              <div className="ema-panel">

                <p className="ema-muted">
                  {loadingAnnouncements
                    ? "Chargement des actualités..."
                    : "Aucune publication récente."}
                </p>

              </div>
            )}

            {/* ===============================================
                ACTUALITÉS RÉCENTES
            ================================================ */}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >

              {sideAnnouncements.length > 0 ? (
                sideAnnouncements.map((announcement) => (
                  <Link
                    key={announcement.id}
                    to={
                      announcement.link ||
                      "/announcements"
                    }
                    style={{
                      display: "block",
                      padding: "20px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "16px",
                      background: "#fff",
                      textDecoration: "none",
                      color: "inherit",
                      boxShadow:
                        "0 2px 10px rgba(11,44,95,0.04)",
                      transition:
                        "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform =
                        "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 20px rgba(11,44,95,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform =
                        "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 10px rgba(11,44,95,0.04)";
                    }}
                  >

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "10px",
                      }}
                    >

                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: "#0B2C5F",
                        }}
                      >
                        {announcement.category ||
                          "Actualité"}
                      </span>

                      <span
                        style={{
                          fontSize: "12px",
                          color: "#9CA3AF",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDateFR(
                          announcement.published_at
                        )}
                      </span>

                    </div>

                    <h3
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        color: "#111827",
                        lineHeight: 1.45,
                        fontWeight: 700,
                      }}
                    >
                      {announcement.title}
                    </h3>

                  </Link>
                ))
              ) : (
                <div className="ema-panel">
                  <p className="ema-muted">
                    Aucune autre publication récente.
                  </p>
                </div>
              )}

              <Link
                to="/announcements"
                className="ema-btn ema-btn-primary"
                style={{
                  width: "100%",
                  marginTop: "auto",
                }}
              >
                Toutes les publications →
              </Link>

            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          CHIFFRES CLÉS
      ====================================================== */}

      <section className="ema-section ema-section-alt">
        <div className="ema-container">

          <h2 className="ema-h2">
            L’École en chiffres
          </h2>

          <p className="ema-lead">
            Quelques indicateurs sur les activités de l’École de la Magistrature.
          </p>

          <div className="ema-stats">

            <Stat
              label="Promotions formées"
              value="8"
            />

            <Stat
              label="Apprenants accompagnés"
              value="363"
            />

            <Stat
              label="Formateurs mobilisés"
              value="30+"
            />

            <Stat
              label="Partenariats"
              value="4"
            />

          </div>

        </div>
      </section>

      {/* =====================================================
          PARTENAIRES
      ====================================================== */}

      <section className="ema-section">
        <div className="ema-container">

          <div className="ema-section-head">

            <div>

              <p className="ema-kicker">
                Coopération
              </p>

              <h2 className="ema-h2">
                Partenaires institutionnels
              </h2>

              <p className="ema-lead">
                L’École de la Magistrature développe ses activités en collaboration
                avec des institutions nationales et des partenaires internationaux.
              </p>

            </div>

          </div>

          <div className="ema-partners">

            <div
              className="ema-partner"
              aria-label="International Association of Women Judges"
            >
              <img
                src="/partenaires/iawj.jpg"
                alt="IAWJ"
              />
            </div>

            <div
              className="ema-partner"
              aria-label="Office des Nations Unies contre la drogue et le crime"
            >
              <img
                src="/partenaires/onudc.png"
                alt="ONUDC"
              />
            </div>

            <div
              className="ema-partner"
              aria-label="Partenaire institutionnel"
            >
              <img
                src="/partenaires/renes.jpeg"
                alt="Partenaire institutionnel"
              />
            </div>

            <div
              className="ema-partner"
              aria-label="Programme des Nations Unies pour le développement"
            >
              <img
                src="/partenaires/pnud.png"
                alt="PNUD"
              />
            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          CTA FINAL
      ====================================================== */}

      <section className="ema-cta">
        <div className="ema-container ema-cta-box">

          <div>

            <h2 className="ema-h2">
              Prêt à déposer votre candidature ?
            </h2>

            <p className="ema-lead">
              Créez votre compte, téléversez vos pièces et suivez l’évolution
              de votre dossier.
            </p>

          </div>

          <div className="ema-cta-actions">

            <Link
              className="ema-btn ema-btn-primary"
              to="/register"
            >
              Créer un compte
            </Link>

            <Link
              className="ema-btn ema-btn-secondary"
              to="/login"
            >
              Se connecter
            </Link>

          </div>

        </div>
      </section>

    </main>
  );
}

/* =========================================================
   COMPONENTS
========================================================= */

function PersonCard({
  role,
  name,
  title,
  summary,
  photo,
  to,
  photoPosition = "center center",
}) {
  return (
    <div className="ema-card ema-person">

      <div className="ema-person-top">

        <img
          className="ema-person-photo"
          src={photo}
          alt={`${role} - ${name}`}
          style={{
            objectPosition: photoPosition,
          }}
          onError={(e) => {
            e.currentTarget.src =
              "/gouvernance/placeholder.jpg";
          }}
        />

        <span className="ema-person-role">
          {role}
        </span>

      </div>

      <div className="ema-person-body">

        <div className="ema-person-name">
          {name}
        </div>

        <div className="ema-muted">
          {title}
        </div>

        {summary && (
          <div
            className="ema-muted"
            style={{
              marginTop: 8,
            }}
          >
            {summary}
          </div>
        )}

        <div className="ema-person-actions">

          <Link
            className="ema-btn ema-btn-secondary"
            to={to}
          >
            Voir profil
          </Link>

          <a
            className="ema-btn ema-btn-ghost"
            href="mailto:contact@ema.edu.ht"
          >
            Contacter
          </a>

        </div>

      </div>

    </div>
  );
}

function QuickCard({
  title,
  text,
  to,
  cta,
}) {
  return (
    <div className="ema-card">

      <h3 className="ema-card-title">
        {title}
      </h3>

      <p className="ema-card-text">
        {text}
      </p>

      <Link
        className="ema-link"
        to={to}
      >
        {cta} →
      </Link>

    </div>
  );
}

function ProgramCard({
  title,
  text,
  to,
}) {
  return (
    <div className="ema-card">

      <h3 className="ema-card-title">
        {title}
      </h3>

      <p className="ema-card-text">
        {text}
      </p>

      <Link
        className="ema-link"
        to={to}
      >
        En savoir plus →
      </Link>

    </div>
  );
}

function TimelineStep({
  n,
  title,
  text,
  to,
  cta,
}) {
  return (
    <div className="ema-step">

      <div className="ema-step-num">
        {n}
      </div>

      <div>

        <h3 className="ema-h3">
          {title}
        </h3>

        <p className="ema-paragraph">
          {text}
        </p>

        <Link
          className="ema-link"
          to={to}
        >
          {cta} →
        </Link>

      </div>

    </div>
  );
}

function Stat({
  label,
  value,
}) {
  return (
    <div className="ema-stat">

      <div className="ema-stat-value">
        {value}
      </div>

      <div className="ema-stat-label">
        {label}
      </div>

    </div>
  );
}